/**
 * Session Performance Tab Component
 * Performance analysis by market session (Asian, European, US, etc.)
 */

import React, { useCallback, useEffect, useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { DataTable } from "../../../../components/ui/data-table";
import { useTheme } from "next-themes";
import {
  Box,
  Text,
  useColorModeValue,
  SimpleGrid,
  VStack,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
  HStack,
  Badge,
} from "@chakra-ui/react";
import {
  Card as UiCard,
  CardContent,
  CardHeader as UiCardHeader,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { StatisticsCardHeadline, STATS_FONT_FAMILY, STATS_MUTED_TEXT_CLASS } from "./statisticsCardHeadline";
import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import CardHeader from "../../../../components/Card/CardHeader";
import { SessionPerformanceResponse, TradeStatisticsFilters } from "../../../../types/trade";
import { useSessionPerformance } from "../../../../hooks/useTradeStatistics";
import { useStatisticsWidgetErrorGate } from "../../../../hooks/useStatisticsWidgetErrorGate";
import { StatisticsInlineError } from "../../../../components/errors/StatisticsInlineError";
import { formatSessionDisplayName } from "../../../../utils/sessionDisplayName";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import SignedPnLBarChart from "./StatsSignedPnlBarChart";
import {
  STATISTICS_NO_DATA_TIMEFRAME_MESSAGE,
  StatisticsChartTooltipRow,
} from "./statisticsChartTooltip";
import { StatisticsTabEmptyState, isStatisticsFetchPending, StatisticsTabLoadingState } from "./statisticsEmptyState";
import {
  PNL_NEGATIVE_COLOR,
  PNL_POSITIVE_COLOR,
  pnlBarFill,
  type SignedPnLBarDatum,
} from "./statsSignedPnlBarUtils";
import {
  statisticsShadcnBarChartAreaClassName,
  statisticsShadcnCardClassName,
  statisticsShadcnCardContentClassName,
  statisticsShadcnTimeAnalysisChartContainerClassName,
  statisticsTimeAnalysisBarChartMargin,
} from "./statisticsShadcnCardShell";

type SessionChartDatum = SignedPnLBarDatum & {
  winRate: number;
  tradeCount: number;
};

type SessionRow = {
  session_name: string;
  total_pnl?: number;
  win_rate?: number;
  trade_count?: number;
};

function sessionsToSignedBarChartData(sessions: SessionRow[]): SessionChartDatum[] {
  return [...sessions]
    .sort((a, b) => (b.total_pnl || 0) - (a.total_pnl || 0))
    .map((session) => {
      const pnl = session.total_pnl ?? 0;
      return {
        label: formatSessionDisplayName(session.session_name),
        pnl,
        fill: pnlBarFill(pnl),
        winRate: session.win_rate ?? 0,
        tradeCount: session.trade_count ?? 0,
      };
    });
}

const sessionSignedPnlChartProps = {
  className: statisticsShadcnTimeAnalysisChartContainerClassName,
  chartMargin: statisticsTimeAnalysisBarChartMargin,
  symmetricYAxis: true as const,
};

function sessionPerformanceHasData(
  data: SessionPerformanceResponse | null | undefined
): boolean {
  if (!data) return false;
  if ((data.summary?.total_trades ?? 0) > 0) return true;
  return (data.sessions ?? []).some((session) => (session.trade_count ?? 0) > 0);
}

interface SessionPerformanceTabProps {
  filters?: TradeStatisticsFilters;
  /** When provided, sections show Selected period and Comparison period cards */
  comparisonFilters?: TradeStatisticsFilters;
}

const SessionPerformanceTab: React.FC<SessionPerformanceTabProps> = ({ filters, comparisonFilters }) => {
  const { formatMoney } = useDisplayCurrency();
  const { resolvedTheme } = useTheme();
  const themeClass = resolvedTheme === "dark" ? "dark" : "";
  const textColor = resolvedTheme === "dark" ? "white" : "#575757";
  const sessionCaptionOpacity = resolvedTheme === "dark" ? 1 : 0.8;
  const sessionCaptionProps = {
    "data-session-stat-caption": "",
    fontSize: { base: "10px", sm: "11px", md: "12px" } as const,
    color: textColor,
    fontFamily: STATS_FONT_FAMILY,
    fontWeight: "medium" as const,
    textTransform: "uppercase" as const,
    opacity: sessionCaptionOpacity,
  };
  const cardBg = useColorModeValue("white", "#151517");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const hasComparison = Boolean(comparisonFilters);

  const { data, error, errorAction } = useSessionPerformance(filters);
  const { showWidgetError, widgetError, widgetAction } = useStatisticsWidgetErrorGate(error, errorAction);
  const { data: comparisonData, error: comparisonError } = useSessionPerformance(
    comparisonFilters,
    { enabled: hasComparison }
  );

  const formatPercent = useCallback((value: number) => {
    return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
  }, []);

  const sessionBarChartData = useMemo(
    () => sessionsToSignedBarChartData(data?.sessions ?? []),
    [data?.sessions]
  );

  const comparisonSessionBarChartData = useMemo(
    () => sessionsToSignedBarChartData(comparisonData?.sessions ?? []),
    [comparisonData?.sessions]
  );

  const sessionTooltipExtra = useCallback(
    (row: SignedPnLBarDatum) => {
      const sessionRow = row as SessionChartDatum;
      return (
        <>
          <StatisticsChartTooltipRow label="Win Rate" value={formatPercent(sessionRow.winRate)} />
          <StatisticsChartTooltipRow label="Trades" value={sessionRow.tradeCount} />
        </>
      );
    },
    [formatPercent]
  );

  const renderSessionComparisonChart = (chartData: SessionChartDatum[]) => {
    if (chartData.length === 0) {
      return <StatisticsTabEmptyState compact />;
    }

    return (
      <div className={cn(statisticsShadcnBarChartAreaClassName, themeClass)}>
        <SignedPnLBarChart
          {...sessionSignedPnlChartProps}
          data={chartData}
          renderTooltipExtra={sessionTooltipExtra}
        />
      </div>
    );
  };

  const breakdownColumns: ColumnDef<any, unknown>[] = useMemo(() => [
    {
      accessorKey: "session_name",
      header: "Session",
      enableSorting: false,
      cell: ({ row }) => (
        <span className={STATS_MUTED_TEXT_CLASS}>
          {formatSessionDisplayName(row.original.session_name)}
        </span>
      ),
    },
    {
      accessorKey: "trade_count",
      header: "Trades",
      enableSorting: false,
      cell: ({ row }) => (
        <span className={STATS_MUTED_TEXT_CLASS}>
          {row.original.trade_count}
        </span>
      ),
    },
    {
      accessorKey: "win_rate",
      header: "Win Rate",
      enableSorting: false,
      cell: ({ row }) => (
        <span className={STATS_MUTED_TEXT_CLASS}>
          {row.original.trade_count > 0 ? `${row.original.win_rate.toFixed(2)}%` : "N/A"}
        </span>
      ),
    },
    {
      accessorKey: "total_pnl",
      header: "Total P&L",
      enableSorting: false,
      cell: ({ row }) => (
        <span
          data-trade-log-pnl
          className="font-[family-name:var(--font-primary)] text-sm font-medium"
          style={{ color: row.original.total_pnl >= 0 ? "#06E363" : "#E00101" }}
        >
          {formatMoney(row.original.total_pnl)}
        </span>
      ),
    },
    {
      accessorKey: "average_pnl",
      header: "Avg P&L",
      enableSorting: false,
      cell: ({ row }) => (
        <span
          data-trade-log-pnl
          className="font-[family-name:var(--font-primary)] text-sm font-medium"
          style={{ color: row.original.average_pnl >= 0 ? "#06E363" : "#E00101" }}
        >
          {row.original.trade_count > 0 ? formatMoney(row.original.average_pnl) : "N/A"}
        </span>
      ),
    },
    {
      accessorKey: "largest_win",
      header: "Largest Win",
      enableSorting: false,
      cell: ({ row }) => (
        <span
          data-trade-log-pnl
          className="font-[family-name:var(--font-primary)] text-sm font-medium"
          style={{ color: "#06E363" }}
        >
          {row.original.largest_win > 0 ? formatMoney(row.original.largest_win) : "N/A"}
        </span>
      ),
    },
    {
      accessorKey: "largest_loss",
      header: "Largest Loss",
      enableSorting: false,
      cell: ({ row }) => (
        <span
          data-trade-log-pnl
          className="font-[family-name:var(--font-primary)] text-sm font-medium"
          style={{ color: "#E00101" }}
        >
          {row.original.largest_loss < 0 ? formatMoney(row.original.largest_loss) : "N/A"}
        </span>
      ),
    },
    {
      accessorKey: "profit_factor",
      header: "Profit Factor",
      enableSorting: false,
      cell: ({ row }) => (
        <span className={STATS_MUTED_TEXT_CLASS}>
          {row.original.profit_factor !== null ? row.original.profit_factor.toFixed(2) : "N/A"}
        </span>
      ),
    },
  ], [formatMoney]);

  if (showWidgetError && widgetError) {
    return <StatisticsInlineError message={widgetError} action={widgetAction ?? undefined} />;
  }

  if (isStatisticsFetchPending(data, error)) {
    return <StatisticsTabLoadingState />;
  }

  if (hasComparison && isStatisticsFetchPending(comparisonData, comparisonError)) {
    return <StatisticsTabLoadingState />;
  }

  if (!hasComparison && !sessionPerformanceHasData(data)) {
    return <StatisticsTabEmptyState />;
  }

  if (
    hasComparison &&
    !sessionPerformanceHasData(data) &&
    !sessionPerformanceHasData(comparisonData)
  ) {
    return <StatisticsTabEmptyState />;
  }

  type SessionItem = NonNullable<typeof data>["sessions"][number];

  const getSessionHighlights = (resp: typeof data) => {
    if (!resp?.sessions?.length) return { bestSession: null, worstSession: null, mostActiveSession: null, bestWinRateSession: null, bestAvgPnLSession: null };
    const sessions = resp.sessions;
    const bestSession = resp.summary?.best_session ? sessions.find(s => s.session_id === resp.summary.best_session) ?? null : null;
    const worstSession = resp.summary?.worst_session ? sessions.find(s => s.session_id === resp.summary.worst_session) ?? null : null;
    const mostActiveSession = sessions.reduce((prev, current) => (current.trade_count > prev.trade_count) ? current : prev);
    const bestWinRateSession = sessions.reduce((prev, current) => (current.win_rate > prev.win_rate) ? current : prev);
    const bestAvgPnLSession = sessions.reduce((prev, current) => (current.average_pnl > prev.average_pnl) ? current : prev);
    return { bestSession, worstSession, mostActiveSession, bestWinRateSession, bestAvgPnLSession };
  };

  const renderTopCards = (resp: typeof data) => {
    const { bestSession: best, worstSession: worst, mostActiveSession: most, bestWinRateSession: winRate, bestAvgPnLSession: avgPnL } = getSessionHighlights(resp);
    if (!sessionPerformanceHasData(resp)) {
      return <StatisticsTabEmptyState compact />;
    }
    return (
      <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4}>
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} boxShadow="none">
          <CardBody>
            <VStack align="start" spacing={1}>
              <StatisticsCardHeadline>Best Session</StatisticsCardHeadline>
              <Text fontSize="14px" fontWeight="500" color={textColor} fontFamily={STATS_FONT_FAMILY}>{best ? formatSessionDisplayName(best.session_name) : "N/A"}</Text>
              {best && (
                <>
                  <Box>
                    <Text
                      fontSize="xl"
                      fontWeight="700"
                      fontFamily={STATS_FONT_FAMILY}
                      color={best.total_pnl >= 0 ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR}
                      lineHeight="1"
                    >
                      {formatMoney(best.total_pnl)}
                    </Text>
                  </Box>
                  <Text {...sessionCaptionProps}>
                    {best.trade_count} trades | {formatPercent(best.win_rate)} win rate
                  </Text>
                </>
              )}
            </VStack>
          </CardBody>
        </Card>
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} boxShadow="none">
          <CardBody>
            <VStack align="start" spacing={1}>
              <StatisticsCardHeadline>Worst Session</StatisticsCardHeadline>
              <Text fontSize="14px" fontWeight="500" color={textColor} fontFamily={STATS_FONT_FAMILY}>{worst ? formatSessionDisplayName(worst.session_name) : "N/A"}</Text>
              {worst && (
                <>
                  <Box>
                    <Text
                      fontSize="xl"
                      fontWeight="700"
                      fontFamily={STATS_FONT_FAMILY}
                      color={worst.total_pnl >= 0 ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR}
                      lineHeight="1"
                    >
                      {formatMoney(worst.total_pnl)}
                    </Text>
                  </Box>
                  <Text {...sessionCaptionProps}>
                    {worst.trade_count} trades | {formatPercent(worst.win_rate)} win rate
                  </Text>
                </>
              )}
            </VStack>
          </CardBody>
        </Card>
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} boxShadow="none">
          <CardBody>
            <VStack align="start" spacing={1}>
              <StatisticsCardHeadline>Most Active Session</StatisticsCardHeadline>
              <Text fontSize="14px" fontWeight="500" color={textColor} fontFamily={STATS_FONT_FAMILY}>{most ? formatSessionDisplayName(most.session_name) : "N/A"}</Text>
              {most && (
                <>
                  <Box>
                    <Text fontSize="xl" fontWeight="700" fontFamily={STATS_FONT_FAMILY} color={textColor} lineHeight="1">
                      {most.trade_count} trades
                    </Text>
                  </Box>
                  <Flex {...sessionCaptionProps} gap={1} flexWrap="wrap">
                    <Text as="span" color={textColor}>
                      {formatMoney(most.total_pnl)}
                    </Text>
                    <Text as="span" color={textColor}>
                      P&L |
                    </Text>
                    <Text as="span" color={textColor}>
                      {formatPercent(most.win_rate)} win rate
                    </Text>
                  </Flex>
                </>
              )}
            </VStack>
          </CardBody>
        </Card>
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} boxShadow="none">
          <CardBody>
            <VStack align="start" spacing={1}>
              <StatisticsCardHeadline>Best Win Rate Session</StatisticsCardHeadline>
              <Text fontSize="14px" fontWeight="500" color={textColor} fontFamily={STATS_FONT_FAMILY}>{winRate ? formatSessionDisplayName(winRate.session_name) : "N/A"}</Text>
              {winRate && (
                <>
                  <Box>
                    <Text fontSize="xl" fontWeight="700" fontFamily={STATS_FONT_FAMILY} color={textColor} lineHeight="1">
                      {formatPercent(winRate.win_rate)}
                    </Text>
                  </Box>
                  <Flex {...sessionCaptionProps} gap={1} flexWrap="wrap">
                    <Text as="span" color={textColor}>
                      {winRate.trade_count} trades |
                    </Text>
                    <Text as="span" color={textColor}>
                      {formatMoney(winRate.total_pnl)}
                    </Text>
                    <Text as="span" color={textColor}>
                      P&L
                    </Text>
                  </Flex>
                </>
              )}
            </VStack>
          </CardBody>
        </Card>
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} boxShadow="none">
          <CardBody>
            <VStack align="start" spacing={1}>
              <StatisticsCardHeadline>Best Avg P&L Session</StatisticsCardHeadline>
              <Text fontSize="14px" fontWeight="500" color={textColor} fontFamily={STATS_FONT_FAMILY}>{avgPnL ? formatSessionDisplayName(avgPnL.session_name) : "N/A"}</Text>
              {avgPnL && (
                <>
                  <Box>
                    <Text
                      fontSize="xl"
                      fontWeight="700"
                      fontFamily={STATS_FONT_FAMILY}
                      color={avgPnL.average_pnl >= 0 ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR}
                      lineHeight="1"
                    >
                      {formatMoney(avgPnL.average_pnl)}
                    </Text>
                  </Box>
                  <Text {...sessionCaptionProps}>
                    {avgPnL.trade_count} trades | {formatPercent(avgPnL.win_rate)} win rate
                  </Text>
                </>
              )}
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>
    );
  };

  const getSortedSessions = (sessions: SessionItem[]) =>
    sessions.slice().sort((a, b) => (b.total_pnl || 0) - (a.total_pnl || 0));

  return (
    <VStack spacing={6} align="stretch">
      {hasComparison ? (
        <>
          {/* Top cards: Selected then Comparison */}
          <Box data-statistics-card-surface="" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={4} bg={cardBg}>
            {renderTopCards(data)}
          </Box>
          <Box data-statistics-card-surface="" border="2px solid" borderColor="#F49113" borderRadius="lg" p={4} bg={cardBg}>
            {renderTopCards(comparisonData)}
          </Box>

          {/* Session Performance Comparison chart: Selected then Comparison */}
          <Box data-statistics-card-surface="" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={4} bg={cardBg}>
            <StatisticsCardHeadline mb="6px">Session Performance Comparison</StatisticsCardHeadline>
            {renderSessionComparisonChart(sessionBarChartData)}
          </Box>
          <Box data-statistics-card-surface="" border="2px solid" borderColor="#F49113" borderRadius="lg" p={4} bg={cardBg}>
            <StatisticsCardHeadline mb="6px">Session Performance Comparison</StatisticsCardHeadline>
            {renderSessionComparisonChart(comparisonSessionBarChartData)}
          </Box>

          {/* Session Analysis Breakdown table: Selected then Comparison */}
          <Box data-statistics-card-surface="" border="2px solid" borderColor="#38BBD1" borderRadius="lg" p={4} bg={cardBg}>
            <div className="mb-4">
              <StatisticsCardHeadline>Session Analysis Breakdown</StatisticsCardHeadline>
            </div>
            <DataTable
              columns={breakdownColumns}
              data={data?.sessions ? getSortedSessions(data.sessions) : []}
              showPagination={false}
              emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
            />
          </Box>
          <Box data-statistics-card-surface="" border="2px solid" borderColor="#F49113" borderRadius="lg" p={4} bg={cardBg}>
            <div className="mb-4">
              <StatisticsCardHeadline>Session Analysis Breakdown</StatisticsCardHeadline>
            </div>
            <DataTable
              columns={breakdownColumns}
              data={comparisonData?.sessions ? getSortedSessions(comparisonData.sessions) : []}
              showPagination={false}
              emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
            />
          </Box>
        </>
      ) : (
        <>
          {renderTopCards(data)}
          <UiCard
            data-statistics-shadcn-card
            className={cn(statisticsShadcnCardClassName, themeClass)}
            style={{ fontFamily: STATS_FONT_FAMILY }}
          >
            <UiCardHeader className="px-[22px] pb-4 pt-[22px]">
              <StatisticsCardHeadline>Session Performance Comparison</StatisticsCardHeadline>
            </UiCardHeader>
            <CardContent className={cn(statisticsShadcnCardContentClassName, "pt-0")}>
              {renderSessionComparisonChart(sessionBarChartData)}
            </CardContent>
          </UiCard>
          <div data-statistics-shadcn-card className="rounded-[6px] border border-border bg-background p-4">
            <div className="mb-4">
              <StatisticsCardHeadline>Session Analysis Breakdown</StatisticsCardHeadline>
            </div>
            <DataTable
              columns={breakdownColumns}
              data={data?.sessions ? getSortedSessions(data.sessions) : []}
              showPagination={false}
              emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
            />
          </div>
        </>
      )}
    </VStack>
  );
};

export default SessionPerformanceTab;
